import * as React from "react"
import * as moment from "moment"
import {SingleDatePicker} from "react-dates"
import "react-dates/lib/css/_datepicker.css"
import {Booker} from "../model/BookingTypes"
import {DropdownProps, Form, Loader, Segment} from "semantic-ui-react"
import {BookingsListTable} from "./BookingsListTable"
import {
  BasicDriverFragment,
  Booker as BOOKER,
  BookingsSeek,
  BookingsState,
} from "../graphql/queries"
import {assertBookingState, assertNonNull} from "../tsutil"
import {bookingDateDisplayFormat} from "../model/time"
import {BookerPicker} from "./BookerPicker"
import {DriverPickerField} from "./DriverPicker"
import {errorUnionToEither, handleError} from "../graphql/errors"
import {WithModalStore} from "../App"
import {GraphQL} from "../graphql"

interface BookingStateOption {
  text: string
  value: BookingsState
}

const bookingStateOptions: Array<BookingStateOption> = [
  {text: "All", value: BookingsState.ALL},
  {text: "Pending", value: BookingsState.PENDING},
  {text: "Done", value: BookingsState.DONE},
  {text: "Invoiced", value: BookingsState.INVOICED},
  {text: "Cancelled", value: BookingsState.CANCELLED},
  {text: "Hidden", value: BookingsState.HIDDEN},
]

export interface BookingListFilter {
  bookingsState: BookingsState
  booker: Booker | null
  driver: BasicDriverFragment | null
  fromDate: moment.Moment
}

export interface BookingsListState {
  filter: BookingListFilter
  seek: BookingsSeek | null
}

export const defaultBookingsListState: () => BookingsListState = () => ({
  filter: {
    bookingsState: BookingsState.ALL,
    booker: null,
    driver: null,
    fromDate: moment(),
  },
  seek: null,
})

interface OwnProps extends WithModalStore {
  state: BookingsListState
  setState: (state: Partial<BookingsListState>) => void
}

type Props = WithBookerProps

class BookingsList extends React.Component<Props, {editingDate: boolean}> {
  constructor(props: Props) {
    super(props)

    this.state = {
      editingDate: false,
    }
  }

  handleSeek = (seekTo: BookingsSeek) => {
    this.props.setState({seek: seekTo})
  }

  setFilter = (filter: Partial<BookingListFilter>) => {
    this.props.setState({
      filter: {...this.props.state.filter, ...filter},
      seek: null,
    })
  }

  handleBookingState = (e: React.SyntheticEvent<HTMLElement>, selected: DropdownProps) =>
    this.setFilter({bookingsState: assertBookingState(selected.value)})

  handleBooker = (booker: Booker | null) => this.setFilter({booker})

  handleDriver = (driver: BasicDriverFragment | null) => this.setFilter({driver})

  handleFromDate = (date: moment.Moment | null) => {
    this.setFilter({fromDate: assertNonNull(date)})
  }

  handleDateEdit = ({focused}: {focused: boolean | null}) =>
    this.setState({editingDate: Boolean(focused)})

  handleToday = () => this.setFilter({fromDate: moment()})

  render() {
    const {
      seek,
      filter,
      filter: {bookingsState, booker: filterBooker, driver, fromDate},
    } = this.props.state
    const {booker, modalStore} = this.props
    const {editingDate} = this.state

    const isAdmin = Boolean(booker && booker.isAdmin)

    if (booker === null) {
      return <div>Please log in to view your bookings.</div>
    }
    return (
      <Segment.Group>
        <Segment inverted color="olive">
          <Form>
            <Form.Group>
              {isAdmin && (
                <Form.Field>
                  <label>Booking state</label>
                  <Form.Dropdown
                    name="bookingState"
                    onChange={this.handleBookingState}
                    selection={true}
                    value={bookingsState}
                    options={bookingStateOptions}
                  />
                </Form.Field>
              )}
              <Form.Group>
                <Form.Field>
                  <label>From date</label>
                  <SingleDatePicker
                    id="2"
                    date={fromDate}
                    isOutsideRange={() => false}
                    onDateChange={this.handleFromDate}
                    numberOfMonths={1}
                    readOnly
                    focused={editingDate}
                    withPortal={false}
                    onFocusChange={this.handleDateEdit}
                    hideKeyboardShortcutsPanel
                    displayFormat={bookingDateDisplayFormat}
                  />
                </Form.Field>
                <Form.Field>
                  <label>&nbsp;</label>
                  <Form.Button compact size="small" onClick={this.handleToday}>
                    Today
                  </Form.Button>
                </Form.Field>
              </Form.Group>
            </Form.Group>
            {isAdmin && (
              <Form.Group>
                <Form.Field>
                  <label>Booker</label>
                  <BookerPicker
                    onBooker={this.handleBooker}
                    booker={filterBooker}
                    disabled={false}
                    modalStore={modalStore}
                    onBookerFormOpen={null}
                    bookerFormOpen={false}
                  />
                </Form.Field>
                <DriverPickerField driver={driver} onDriver={this.handleDriver} />
              </Form.Group>
            )}
          </Form>
        </Segment>
        <Segment inverted color="olive">
          <BookingsListTable
            isAdmin={isAdmin}
            isDriver={booker.isDriver}
            filter={filter}
            seek={seek}
            onSeek={this.handleSeek}
          />
        </Segment>
      </Segment.Group>
    )
  }
}

type WithBookerProps = OwnProps & {booker: Booker | null}

function WithBooker(props: OwnProps) {
  return (
    <GraphQL.Query
      query={BOOKER}
      variables={{}}
      render={({data}) => {
        if (data !== null) {
          return handleError(errorUnionToEither(data.booker), (data) => (
            <BookingsList {...props} booker={data.booker} />
          ))
        }
        // This should not happen, as Booker is loaded in InitializeSession
        return <Loader active />
      }}
    />
  )
}

export {WithBooker as BookingsList}



// WEBPACK FOOTER //
// ./src/components/BookingsList.tsx